// web/src/components/browser/FolderTile.tsx
import { cn } from "../../lib/utils";
import { useNavigation } from "../../stores/navigation";
import { useLongPress } from "../../hooks/useLongPress";

interface Props {
  name: string;
  itemCount?: number;
  onOpen: () => void;
}

export function FolderTile({ name, itemCount, onOpen }: Props) {
  const selected = useNavigation((s) => s.selection.has(name));
  const active = useNavigation((s) => s.selectionActive);
  const toggle = useNavigation((s) => s.toggleSelection);
  const activate = useNavigation((s) => s.activateSelection);
  const { didLongPress, ...handlers } = useLongPress(() => activate(name));

  const countLabel =
    itemCount === undefined
      ? null
      : itemCount === 0
        ? "Empty"
        : `${itemCount} ${itemCount === 1 ? "item" : "items"}`;

  return (
    <button
      {...handlers}
      onClick={(e) => {
        if (didLongPress()) return;
        if (active) {
          toggle(name);
          return;
        }
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          toggle(name);
          return;
        }
        onOpen();
      }}
      className={cn(
        "relative flex aspect-square flex-col items-center justify-center gap-2 rounded-lg border border-border bg-white/5 p-3 text-center text-xs text-text-soft hover:border-accent-soft",
        selected && "border-accent bg-accent-bg",
      )}
    >
      <div className="relative h-5 w-7 rounded-t-sm rounded-b-md bg-accent-soft/70">
        <span className="absolute -top-1 left-0 h-1 w-3 rounded-t-sm bg-accent-soft/70" />
      </div>
      <span className="line-clamp-2 text-center">{name}</span>
      {countLabel && (
        <span className="font-mono text-[9px] uppercase tracking-wider text-text-dim">
          {countLabel}
        </span>
      )}
      {(active || selected) && (
        <span
          aria-label={selected ? "Selected" : "Not selected"}
          className={cn(
            "absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full border",
            selected
              ? "border-accent bg-accent text-primary-foreground"
              : "border-border bg-bg-raised",
          )}
        >
          {selected && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L20 7" />
            </svg>
          )}
        </span>
      )}
    </button>
  );
}
